// docs/35 review: the gallery has no server, so it keeps a room of creatures in wasm
// (`WildPreview`, the module's own tick) and ticks it on the device clock. GALLERY-ONLY.
import { wasmModule } from '../../core/sim/wasm';
import { landOfKit } from '../../core/audio/ambience';
import { TICK_MS } from './constants';
import type { Snapshot } from '../../core/sim/types';
import type { WildRow } from '../../core/wildlife/motion';
import type { Weather, Season } from '../weather';

const GROUND: Record<string, string> = { Open: 'Open', Spawn: 'Open', Tunnel: 'Open', Bad: 'Rough', Wall: 'Wall', Water: 'Water', Deep: 'Deep', Thicket: 'Thicket', Chasm: 'Chasm', Void: 'Void' };
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export interface PreviewBody { unit: number; x: number; y: number; size: number; hero: boolean }

/** `fk_wild::Habitat` from a gallery board and its stand-in units. */
export function habitatOf(board: Snapshot['board'], bodies: PreviewBody[]): unknown {
  return {
    w: board.w, h: board.h, bodies,
    cells: board.tiles.map((t) => (t.terrain === 'Door' ? (t.doorOpen ? 'Open' : 'Wall') : GROUND[t.terrain] ?? 'Open')),
  };
}

/** `fk_wild::Room` from the gallery's kit, hour, weather and season. */
export function roomOf(kit: string, hour: number, weather: Weather, season?: Season): unknown {
  const land = landOfKit(kit);
  return {
    land: land === 'Plain' ? 'Hall' : land, kit: land === 'Plain' ? 'keep' : kit, hour: Math.floor(((hour % 24) + 24) % 24),
    weather: cap(weather.kind), intensity: Math.round(weather.intensity * 1000), season: season ? cap(season) : null,
  };
}

export class WildPreviewRoom {
  private room: InstanceType<ReturnType<typeof wasmModule>['WildPreview']>;
  private next = 0;
  rows: WildRow[] = [];

  /** Starts the room `warm` ms ago so creatures are already about. */
  constructor(habitat: unknown, room: unknown, seed: number, now: number, warm = 40_000) {
    const W = wasmModule();
    this.room = new W.WildPreview(habitat, room, BigInt(seed));
    for (let t = now - warm; t <= now; t += TICK_MS) this.room.tick(t);
    this.next = now + TICK_MS;
    this.read();
  }

  private read(): void { this.rows = (this.room.rows() as Array<WildRow & { untilMs: number }>).map((r) => ({ id: r.id, species: r.species, seed: r.seed >>> 0, legs: r.legs, leaving: r.leaving, ...(r.attend !== undefined && r.attend !== null ? { attend: r.attend } : {}) })); }

  update(now: number): void {
    if (now < this.next) return;
    this.next = now + TICK_MS;
    this.room.tick(now);
    this.read();
  }

  /** The units now stand at `bodies` (moved since the last call): the room reacts from `at`. */
  stir(bodies: PreviewBody[], at: number): void { this.room.stir(bodies, at); this.read(); }

  free(): void { this.room.free(); }
}
